import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookMarked } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { booksApi, catalogueReferenceApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { Book } from '@/types/library';
import { orDash } from '@/utils/format';
import { Badge, authorsOf } from './shared';

/**
 * The catalogue.
 *
 * <p>Searching is the server's job. The box here sends a query and shows a page
 * of results; it never holds the catalogue and filters it, because a school with
 * twelve thousand titles would be sending twelve thousand rows to a browser to
 * type in a search box. §44 rules that out in as many words.
 *
 * <p>The search itself is full text over title, subtitle, subject and tags, and
 * tolerates a misspelling — a reader looking for "Achebe" who types "Achebi"
 * still finds it.
 */
export function CataloguePage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('title,asc');
  const manages = can(PERMISSIONS.CATALOGUE_MANAGE);

  const [categoryId, setCategoryId] = useState('');
  const [availableOnly, setAvailableOnly] = useState('');
  const [adding, setAdding] = useState(false);

  const [title, setTitle] = useState('');
  const [isbn, setIsbn] = useState('');
  const [authorNames, setAuthorNames] = useState('');
  const [publisherName, setPublisherName] = useState('');
  const [category, setCategory] = useState('');
  const [year, setYear] = useState('');
  const [language, setLanguage] = useState('English');
  const [subject, setSubject] = useState('');

  const categories = useQuery({
    queryKey: queryKeys.catalogueReference.categories,
    queryFn: () => catalogueReferenceApi.categories(),
  });

  const params = {
    ...table.params,
    ...(categoryId === '' ? {} : { categoryId }),
    ...(availableOnly === '' ? {} : { availableOnly: availableOnly === 'yes' }),
  };

  const books = useQuery({
    queryKey: queryKeys.books.search(params),
    queryFn: () => booksApi.search(params),
  });

  const create = useMutation({
    mutationFn: () =>
      booksApi.create({
        title: title.trim(),
        isbn: isbn.trim() === '' ? undefined : isbn.trim(),
        // Authors typed as free text: a cataloguer working through a delivery
        // should not have to create an author record first. The server matches
        // an existing name or creates one.
        authorNames: authorNames
          .split(',')
          .map((name) => name.trim())
          .filter((name) => name !== ''),
        publisherName: publisherName.trim() === '' ? undefined : publisherName.trim(),
        categoryId: category === '' ? undefined : category,
        publicationYear: year === '' ? undefined : Number(year),
        language: language.trim() === '' ? undefined : language.trim(),
        subject: subject.trim() === '' ? undefined : subject.trim(),
      }),
    onSuccess: (book) => {
      toast.success(`${book.title} catalogued`);
      setAdding(false);
      setTitle('');
      setIsbn('');
      setAuthorNames('');
      setPublisherName('');
      setYear('');
      setSubject('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.books.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.library.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Book>[] = [
    {
      key: 'title',
      header: 'Title',
      sortKey: 'title',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.bookDetail(row.id)}
            className="block text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.title}
          </Link>
          <span className="block text-[11px] text-subtle">{authorsOf(row.authors)}</span>
        </span>
      ),
    },
    {
      key: 'isbn',
      header: 'ISBN',
      render: (row) => <span className="font-mono text-[12px] text-muted">{orDash(row.isbn)}</span>,
    },
    { key: 'category', header: 'Category', render: (row) => orDash(row.categoryName) },
    {
      key: 'publisher',
      header: 'Publisher',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.publisherName)}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.publicationYear)}</span>
        </span>
      ),
    },
    {
      key: 'availability',
      header: 'Copies',
      className: 'text-right',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">
            {row.availability.availableCopies} of {row.availability.totalCopies}
          </span>
          <span className="block text-[11px] text-subtle">
            {row.availability.totalCopies === 0
              ? 'none accessioned'
              : row.availability.availableCopies === 0
                ? row.availability.queueLength > 0
                  ? `${row.availability.queueLength} waiting`
                  : 'all out'
                : 'on the shelf'}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: '',
      render: (row) =>
        row.status === 'ACTIVE' ? null : <Badge tone="neutral">withdrawn</Badge>,
    },
  ];

  return (
    <>
      <ListPage
        title="Catalogue"
        description="Every title the school holds. Searching happens on the server."
        icon={<BookMarked className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => setAdding(true)}>Add a title</Button> : undefined}
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Title, subject, author, tag…',
        }}
        filters={
          <>
            <FilterSelect
              label="Category"
              value={categoryId}
              onChange={(value) => {
                setCategoryId(value);
                table.setPage(0);
              }}
              options={(categories.data ?? []).map((row) => ({
                value: row.id,
                label: row.fullPath,
              }))}
            />
            <FilterSelect
              label="Availability"
              value={availableOnly}
              onChange={(value) => {
                setAvailableOnly(value);
                table.setPage(0);
              }}
              options={[{ value: 'yes', label: 'On the shelf now' }]}
            />
          </>
        }
        pagination={books.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={books.data?.data}
          rowKey={(row) => row.id}
          loading={books.isPending}
          error={books.error}
          onRetry={() => void books.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle={table.searchInput === '' ? 'Nothing catalogued yet' : 'Nothing matched'}
          emptyDescription={
            table.searchInput === ''
              ? 'Add a title to start the catalogue.'
              : 'The search tolerates a misspelling, so this is probably not one.'
          }
        />
      </ListPage>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a title"
        description="One catalogue record per title. Physical copies are accessioned separately."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={title.trim() === '' || create.isPending}
            >
              Catalogue it
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} required />
          <TextField label="ISBN" value={isbn} onChange={(event) => setIsbn(event.target.value)}
            hint="Ten or thirteen digits. The check digit is verified, so a typo is caught here."
          />
          <TextField label="Authors" value={authorNames} onChange={(event) => setAuthorNames(event.target.value)}
            hint="Separated by commas. Names that already exist are matched, not duplicated."
          />
          <TextField label="Publisher" value={publisherName} onChange={(event) => setPublisherName(event.target.value)} />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">
              Category
            </span>
            <select
              className="mt-1 w-full border border-line bg-bg px-3 py-2 text-sm text-ink"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">Uncategorised</option>
              {(categories.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.fullPath}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Year" value={year} onChange={(event) => setYear(event.target.value)} />
            <TextField label="Language" value={language} onChange={(event) => setLanguage(event.target.value)} />
          </div>
          <TextField label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)}
            hint="Searched alongside the title, so a reader can find a book by what it is about."
          />
        </div>
      </Modal>
    </>
  );
}
